/**
 * Client-safe package card view. Kept apart from the async `PackageCard`
 * server shell (which resolves hrefs via server-only routing) so client
 * components like SearchClient can render it without bundling that code.
 */
import Image from "next/image";
import Link from "next/link";
import { Star } from "lucide-react";
import type { RibbonTone } from "@/lib/dto";

/** CMS ribbon tones → on-brand colour pairs. */
const RIBBON_TONES: Record<RibbonTone, string> = {
  gold: "bg-[#F38736] text-white",
  teal: "bg-info text-white",
  rose: "bg-rose-600 text-white",
  navy: "bg-primary text-white",
  danger: "bg-red-600 text-white",
  neutral: "bg-primary text-white",
};

/**
 * Card-specific duration format — matches production's PackageCard.js
 * exactly ("13 Nights 14 Days" for a 14-day trip), which differs from the
 * plainer "14 days" formatDuration() produces elsewhere (trip detail
 * headers, booking module) — not consolidated into the shared helper since
 * those other call sites' real production format hasn't been verified yet.
 */
export function formatCardDuration(value: number, unit: string): string {
  if (unit === "days") return `${value - 1} Nights ${value} Days`;
  return `${value} ${unit}`;
}

// ─── Shared view props ────────────────────────────────────────────────────────

export type PackageCardViewProps = {
  href: string;
  title: string;
  image: string | null;
  imageAlt: string;
  ribbon: string | null;
  rating: number | null;
  reviewCount: number;
  /** Pre-formatted "X Nights Y Days" (or "N hours") — see formatCardDuration. */
  duration: string | null;
  price: string | null;
  priority?: boolean;
  /** Responsive `sizes` for the card image — override when the card renders narrower than the default grid assumes. */
  sizes?: string;
  /** "$X Off" badge + struck-through original price — the homepage's "Deals & Discounts" carousel. */
  discountAmount?: number | null;
  originalPrice?: string | null;
};

const PACKAGE_CARD_SIZES =
  "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";

// ─── Presentational component (sync, client-safe) ─────────────────────────────

/**
 * Matches production's PackageCard.js exactly: rating row (5 stars + "X.XX /
 * NN"), title, a duration+grade icon list ("grade" is a hardcoded "Easy to
 * Hard" literal in production too — not real per-package data), price block
 * with an optional struck-through original price. No hover arrow-button —
 * the whole card is the link.
 */
export function PackageCardView({
  href,
  title,
  image,
  imageAlt,
  ribbon,
  rating,
  reviewCount,
  duration,
  price,
  priority = false,
  sizes = PACKAGE_CARD_SIZES,
  discountAmount,
  originalPrice,
}: PackageCardViewProps) {
  return (
    <Link
      href={href}
      className="item group flex h-full flex-col overflow-hidden rounded bg-paper shadow-[0_0_20px] shadow-black/10"
    >
      <figure className="relative image-slot aspect-[306/310] w-full overflow-hidden rounded">
        {image ? (
          <Image
            src={image}
            alt={imageAlt}
            fill
            loading={priority ? "eager" : undefined}
            sizes={sizes}
            className="object-cover transition-transform duration-300 group-hover:scale-110"
          />
        ) : null}
        {discountAmount ? (
          <span className="absolute top-[.9375rem] right-[.9375rem] z-10 rounded-[3px] bg-warning px-2 py-[3px] text-[.75rem] font-extrabold uppercase text-white">
            ${discountAmount} Off
          </span>
        ) : ribbon ? (
          <span
            className={`absolute top-[.9375rem] right-[.9375rem] z-10 rounded-[3px] px-2 py-[3px] text-[.75rem] font-extrabold uppercase ${RIBBON_TONES.gold}`}
          >
            {ribbon}
          </span>
        ) : null}
      </figure>

      <figcaption className="flex flex-1 flex-col p-2 md:p-6">
        <div className="pb-1">
          {reviewCount > 0 ? (
            <span className="mb-1 flex items-center gap-0.5 text-xs text-muted md:text-sm">
              <i className="ratings__5" aria-hidden></i>
              {(rating ?? 5).toFixed(2)} /{" "}
              {String(reviewCount).padStart(2, "0")}
            </span>
          ) : null}
          <h3 className="line-clamp-2 text-base leading-[1.25] md:text-2xl">
            {title}
          </h3>
        </div>

        <div className="mt-auto flex items-end justify-between pt-1.5 md:pt-3">
          <ul className="text-xs leading-[1.4] sm:text-sm md:text-[0.9375rem] [&>li+li]:mt-2">
            {duration ? (
              <li className="flex items-center">
                <svg
                  className="icon h-5 w-5 text-primary sm:h-6 sm:w-6"
                  aria-hidden
                >
                  <use xlinkHref="/icons.svg#duration" />
                </svg>
                <span className="pl-1.5 text-muted sm:pl-3 md:text-sm">
                  {duration}
                </span>
              </li>
            ) : null}
            <li className="flex items-center">
              <svg
                className="icon h-5 w-5 text-primary sm:h-6 sm:w-6"
                aria-hidden
              >
                <use xlinkHref="/icons.svg#grade" />
              </svg>
              <span className="pl-1.5 text-muted sm:pl-3 md:text-sm">
                Easy to Hard
              </span>
            </li>
          </ul>

          <div className="text-right leading-[1.1] font-secondary">
            {originalPrice ? (
              <span className="block text-xs font-bold text-muted line-through md:text-[0.9375rem] md:font-normal">
                {originalPrice}
              </span>
            ) : null}
            {price ? (
              <span className="text-sm font-bold text-primary md:text-2xl">
                {price}
              </span>
            ) : null}
          </div>
        </div>
      </figcaption>
    </Link>
  );
}
